import { Injectable } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  CanActivate,
  RouterStateSnapshot,
} from '@angular/router';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { LogicielCaisseAuthService } from 'app/services/logiciel-caisse/logiciel-caisse-auth.service';
import {
  redirectToHubAccesLogicielCaisse,
  rewriteLocalhostToHostedUrl,
} from 'app/services/logiciel-caisse/logiciel-caisse-hub-redirect.util';

const LC_HUB_REDIRECT_AT_KEY = 'lc_parametre_hub_redirect_at';
/** Anti double-clic / double canActivate seulement. */
const REDIRECT_DEBOUNCE_MS = 2500;

/**
 * Garde LC sur front-parametre — optimisée pour l’hébergé.
 *
 * Chemin rapide (0 HTTP) : JWT + station déjà en localStorage → accès immédiat,
 * refresh éventuel en arrière-plan. Plus de bootstrap bloquant ni flash onboarding.
 */
@Injectable({ providedIn: 'root' })
export class LogicielCaisseAuthGuard implements CanActivate {
  constructor(private readonly lcAuth: LogicielCaisseAuthService) {}

  canActivate(
    _route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Observable<boolean> {
    const absoluteReturn = rewriteLocalhostToHostedUrl(
      `${window.location.origin}${state.url}`
    );

    // Fast path : session locale fraîche → navigation immédiate (0 HTTP).
    if (this.lcAuth.isReadyForNavigation() || this.lcAuth.hasUsableLocalLcSession()) {
      this.clearRedirectDebounce();
      this.lcAuth.refreshSessionInBackground();
      return of(true);
    }

    // Slow path : pas de session locale → un seul bootstrap.
    return this.lcAuth.ensureAuthenticatedSession({ skipIfLocalFresh: true }).pipe(
      map((ok): boolean => {
        if (ok && this.lcAuth.isAuthenticated()) {
          this.clearRedirectDebounce();
          return true;
        }
        if (this.lcAuth.getLogicielCaisseToken() && this.lcAuth.hasUsableLocalLcSession()) {
          this.clearRedirectDebounce();
          return true;
        }
        if (this.lcAuth.getLogicielCaisseToken()) {
          console.warn(
            '[LC AuthGuard] JWT présent sans session complète — accès autorisé (anti-lenteur)'
          );
          this.clearRedirectDebounce();
          this.lcAuth.refreshSessionInBackground();
          return true;
        }
        this.redirectToHubSetup(absoluteReturn, 'needs_setup');
        return false;
      }),
      catchError((): Observable<boolean> => {
        if (this.lcAuth.getLogicielCaisseToken()) {
          this.clearRedirectDebounce();
          return of(true);
        }
        this.redirectToHubSetup(absoluteReturn, 'needs_setup');
        return of(false);
      })
    );
  }

  private redirectToHubSetup(
    absoluteReturn: string,
    kind: 'needs_password' | 'needs_setup'
  ): void {
    if (this.inRedirectDebounce()) {
      return;
    }
    this.markRedirectDebounce();
    const path =
      kind === 'needs_password'
        ? '/tiers/acces-logiciel-caisse/add'
        : '/tiers/acces-logiciel-caisse/onboarding';
    redirectToHubAccesLogicielCaisse(path, absoluteReturn);
  }

  private inRedirectDebounce(): boolean {
    try {
      const at = Number(sessionStorage.getItem(LC_HUB_REDIRECT_AT_KEY));
      return Number.isFinite(at) && at > 0 && Date.now() - at < REDIRECT_DEBOUNCE_MS;
    } catch {
      return false;
    }
  }

  private markRedirectDebounce(): void {
    try {
      sessionStorage.setItem(LC_HUB_REDIRECT_AT_KEY, String(Date.now()));
    } catch {
      /* ignore */
    }
  }

  private clearRedirectDebounce(): void {
    try {
      sessionStorage.removeItem(LC_HUB_REDIRECT_AT_KEY);
    } catch {
      /* ignore */
    }
  }
}
